import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { BisZumEnde, Modal, SignatureField } from '@symphenia/ui-components'
import { SITE_DOC_SIGNER_ROLES, kurzHash, type SiteDocDetail, type SiteDocPreview, type SiteDocProjectContact, type SiteDocSignerRole } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { cn } from '@/lib/utils'
import { alsDataUrl, declineSiteDoc, fetchSiteDoc, fetchSiteDocPreview, finalizeSiteDoc, presentSiteDoc, type SignatureInput } from '../api/site-docs.api'
import { KanonAnsicht } from './KanonAnsicht'

interface Unterzeichner {
  role: SiteDocSignerRole
  name: string
  function: string
  email: string
  contactId: string | null
  bild: Blob | null
}

const leer = (role: SiteDocSignerRole): Unterzeichner => ({ role, name: '', function: '', email: '', contactId: null, bild: null })
const emailOk = (e: string) => !e.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim())

/** AP-46 9. Schritt 6 — Bestätigungsanzeige im ERP: Nummer, Datum, Prüfwert, QR-Code der Prüfseite. */
export function ErpBestaetigung({ dok }: { dok: SiteDocDetail }) {
  const { t } = useTranslation()
  if (!dok.contentHash || !dok.number) return null
  const titel = dok.status === 'DECLINED' ? t('siteDocs.release.declinedDone') : dok.status === 'AWAITING' ? t('siteDocs.release.linkDone', { email: dok.confirmation?.email ?? '' }) : t('siteDocs.release.signedDone')
  return (
    <div className="space-y-3 text-center">
      <p className="text-lg font-semibold">{titel}</p>
      <dl className="mx-auto max-w-xs space-y-1 text-left text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">{t(`siteDocs.kinds.${dok.kind}`)}</dt>
          <dd className="font-semibold">{dok.number}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">{t('siteDocs.preview.date')}</dt>
          <dd className="font-semibold">{dok.documentDate.slice(0, 10).split('-').reverse().join('.')}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">{t('siteDocs.release.checkValue')}</dt>
          <dd className="font-mono font-semibold">{kurzHash(dok.contentHash)}</dd>
        </div>
      </dl>
      {dok.verifyUrl && (
        <div className="space-y-1">
          <div className="mx-auto w-fit rounded-lg bg-white p-3">
            <QRCodeSVG value={dok.verifyUrl} size={168} />
          </div>
          <p className="text-sm text-muted-foreground">{t('siteDocs.release.qrHint')}</p>
        </div>
      )}
    </div>
  )
}

/**
 * AP-46 9. — **Zur Unterschrift im ERP** (etwa am Tablet im Browser): Nummer vergeben, Vorzeige aus dem
 * kanonischen Stand, „Bestätigen" erst nach dem Lesen bis zum Ende (BD-41), Unterzeichner aus den
 * Kontakten oder eingetippt, Unterschrift auf derselben Fläche wie in der App. Mehrere Pflicht-
 * Unterzeichner unterschreiben nacheinander. Oder: Kunde verweigert, mit Grund.
 */
export function FreigabeDialog({ dok, offen, onClose, onFertig }: { dok: SiteDocDetail; offen: boolean; onClose: () => void; onFertig: (neu: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const [vorschau, setVorschau] = useState<SiteDocPreview | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [gelesen, setGelesen] = useState(false)
  const [gesammelt, setGesammelt] = useState<Unterzeichner[]>([])
  const [aktuell, setAktuell] = useState<Unterzeichner>(leer('CUSTOMER'))
  const [flaeche, setFlaeche] = useState(0)
  const [verweigern, setVerweigern] = useState(false)
  const [grund, setGrund] = useState('')
  // AP-46 22.: Kunde nicht da — er bestätigt im eigenen Browser.
  const [perLink, setPerLink] = useState(false)
  const [empfaenger, setEmpfaenger] = useState<{ name: string; email: string; contactId: string | null }>({ name: '', email: '', contactId: null })
  const linkErlaubt = (dok.allowedReleases ?? []).includes('LINK')
  const [arbeitet, setArbeitet] = useState(false)
  const [fertig, setFertig] = useState<SiteDocDetail | null>(null)

  useEffect(() => {
    if (!offen) return
    let aktiv = true
    setFehler(null)
    setVorschau(null)
    setFertig(null)
    setGesammelt([])
    void (async () => {
      try {
        if (!dok.number) await presentSiteDoc(dok.kind, dok.id)
        const v = await fetchSiteDocPreview(dok.id, 'SIGNATURE')
        if (!aktiv) return
        setVorschau(v)
        setAktuell(leer(v.requiredSigners[0] ?? 'CUSTOMER'))
      } catch (e) {
        if (aktiv) setFehler(fehlertext(e, t))
      }
    })()
    return () => {
      aktiv = false
    }
  }, [offen, dok.id, dok.kind, dok.number, t])

  const vollstaendig = (u: Unterzeichner) => !!u.name.trim() && !!u.bild && emailOk(u.email)
  const alle = [...gesammelt, ...(vollstaendig(aktuell) ? [aktuell] : [])]
  const fehlendeRollen = (vorschau?.requiredSigners ?? []).filter((r) => !alle.some((u) => u.role === r))

  function waehle(k: SiteDocProjectContact) {
    setAktuell((u) => ({ ...u, name: k.name, function: k.function ?? '', email: k.email ?? '', contactId: k.id }))
  }

  function weitere() {
    if (!vollstaendig(aktuell)) return
    setGesammelt((g) => [...g, aktuell])
    const offenRolle = (vorschau?.requiredSigners ?? []).find((r) => r !== aktuell.role && !gesammelt.some((u) => u.role === r))
    setAktuell(leer(offenRolle ?? 'OTHER'))
    setFlaeche((n) => n + 1)
  }

  async function bestaetigen() {
    setArbeitet(true)
    setFehler(null)
    try {
      const unterschriften: SignatureInput[] = await Promise.all(
        alle.map(async (u) => ({ role: u.role, name: u.name.trim(), function: u.function.trim() || null, email: u.email.trim() || null, contactId: u.contactId, image: await alsDataUrl(u.bild!) })),
      )
      await finalizeSiteDoc(dok.kind, dok.id, 'SIGNATURE', unterschriften)
      setFertig(await fetchSiteDoc(dok.id))
    } catch (e) {
      setFehler(fehlertext(e, t))
    } finally {
      setArbeitet(false)
    }
  }

  async function perLinkSenden() {
    setArbeitet(true)
    setFehler(null)
    try {
      await finalizeSiteDoc(dok.kind, dok.id, 'LINK', [], { email: empfaenger.email.trim(), name: empfaenger.name.trim() || null, contactId: empfaenger.contactId })
      setFertig(await fetchSiteDoc(dok.id))
    } catch (e) {
      setFehler(fehlertext(e, t))
    } finally {
      setArbeitet(false)
    }
  }

  async function ablehnen() {
    setArbeitet(true)
    setFehler(null)
    try {
      await declineSiteDoc(dok.kind, dok.id, grund.trim())
      setFertig(await fetchSiteDoc(dok.id))
    } catch (e) {
      setFehler(fehlertext(e, t))
    } finally {
      setArbeitet(false)
    }
  }

  const feld = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm'

  return (
    <Modal
      isOpen={offen}
      onClose={() => (fertig ? onFertig(fertig) : onClose())}
      title={t('siteDocs.release.title')}
      size="lg"
      footer={
        fertig ? (
          <button type="button" onClick={() => onFertig(fertig)} className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground">
            {t('siteDocs.release.close')}
          </button>
        ) : perLink ? (
          <div className="flex w-full justify-end gap-2">
            <button type="button" onClick={() => setPerLink(false)} className="h-10 rounded-md border px-4 text-sm">
              {t('common.cancel')}
            </button>
            <button type="button" disabled={!empfaenger.email.trim() || !emailOk(empfaenger.email) || arbeitet} onClick={() => void perLinkSenden()} className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50">
              {t('siteDocs.release.linkSend')}
            </button>
          </div>
        ) : verweigern ? (
          <div className="flex w-full justify-end gap-2">
            <button type="button" onClick={() => setVerweigern(false)} className="h-10 rounded-md border px-4 text-sm">
              {t('common.cancel')}
            </button>
            <button type="button" disabled={grund.trim().length < 3 || arbeitet} onClick={() => void ablehnen()} className="h-10 rounded-md bg-red-600 px-4 text-sm font-medium text-white disabled:opacity-50">
              {t('siteDocs.release.recordDecline')}
            </button>
          </div>
        ) : (
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={!vorschau} onClick={() => setVerweigern(true)} className="h-10 rounded-md border px-4 text-sm text-red-700 disabled:opacity-50">
                {t('siteDocs.release.declined')}
              </button>
              {linkErlaubt && (
                <button type="button" disabled={!vorschau} onClick={() => setPerLink(true)} className="h-10 rounded-md border px-4 text-sm disabled:opacity-50">
                  {t('siteDocs.release.linkStart')}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!gelesen && vorschau && <span className="text-xs text-muted-foreground">{t('siteDocs.release.readFirst')}</span>}
              <button
                type="button"
                disabled={!vorschau || !gelesen || !alle.length || fehlendeRollen.length > 0 || arbeitet}
                onClick={() => void bestaetigen()}
                className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                {t('siteDocs.release.confirm')}
              </button>
            </div>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {fehler && <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">{fehler}</p>}
        {fertig && <ErpBestaetigung dok={fertig} />}
        {!fertig && !vorschau && !fehler && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
        {!fertig && vorschau && (
          <>
            <BisZumEnde onGelesen={setGelesen} hinweis={t('siteDocs.release.readToEnd')} className="max-h-[45vh]">
              <KanonAnsicht objekt={vorschau.objekt} />
            </BisZumEnde>
            {perLink ? (
              <div className="space-y-2">
                <p className="text-sm">{t('siteDocs.release.linkText')}</p>
                {vorschau.contacts.some((k) => k.email) && (
                  <div className="flex flex-wrap gap-2">
                    {vorschau.contacts
                      .filter((k) => k.email)
                      .map((k) => (
                        <button key={k.id ?? `${k.name}-${k.email}`} type="button" onClick={() => setEmpfaenger({ name: k.name, email: k.email ?? '', contactId: k.id })} className={cn('rounded-md border px-3 py-1.5 text-left text-sm', empfaenger.email === k.email && 'border-primary bg-primary/10')}>
                          {k.name}
                          <span className="text-muted-foreground"> · {k.email}</span>
                        </button>
                      ))}
                  </div>
                )}
                <div className="grid gap-2 sm:grid-cols-2">
                  <input className={feld} placeholder={t('siteDocs.release.name')} value={empfaenger.name} onChange={(e) => setEmpfaenger((x) => ({ ...x, name: e.target.value, contactId: null }))} />
                  <input className={feld} type="email" placeholder={t('siteDocs.release.email')} value={empfaenger.email} onChange={(e) => setEmpfaenger((x) => ({ ...x, email: e.target.value, contactId: null }))} />
                </div>
                {!emailOk(empfaenger.email) && <p className="text-sm text-red-700">{t('siteDocs.release.emailInvalid')}</p>}
              </div>
            ) : verweigern ? (
              <div className="space-y-2">
                <p className="text-sm">{t('siteDocs.release.declineText')}</p>
                <textarea className="min-h-24 w-full rounded-md border border-input bg-background p-3 text-sm" placeholder={t('siteDocs.release.reason')} value={grund} onChange={(e) => setGrund(e.target.value)} />
              </div>
            ) : (
              <section className="space-y-3">
                {gesammelt.length > 0 && (
                  <ul className="space-y-1 text-sm">
                    {gesammelt.map((u, i) => (
                      <li key={i} className="rounded-md border px-3 py-2">
                        ✓ {t(`siteDocs.roles.${u.role}`)}: {u.name}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap gap-2">
                  {SITE_DOC_SIGNER_ROLES.map((r) => (
                    <button key={r} type="button" onClick={() => setAktuell((u) => ({ ...u, role: r }))} className={cn('h-9 rounded-md border px-3 text-sm', aktuell.role === r && 'border-primary bg-primary/10')}>
                      {t(`siteDocs.roles.${r}`)}
                    </button>
                  ))}
                </div>
                {vorschau.contacts.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {vorschau.contacts.map((k) => (
                      <button key={k.id ?? `${k.name}-${k.email ?? ''}`} type="button" onClick={() => waehle(k)} className={cn('rounded-md border px-3 py-1.5 text-left text-sm', aktuell.contactId === k.id && aktuell.name === k.name && 'border-primary bg-primary/10')}>
                        {k.name}
                        {k.function && <span className="text-muted-foreground"> · {k.function}</span>}
                      </button>
                    ))}
                  </div>
                )}
                <div className="grid gap-2 sm:grid-cols-3">
                  <input className={feld} placeholder={t('siteDocs.release.name')} value={aktuell.name} onChange={(e) => setAktuell((u) => ({ ...u, name: e.target.value, contactId: null }))} />
                  <input className={feld} placeholder={t('siteDocs.release.function')} value={aktuell.function} onChange={(e) => setAktuell((u) => ({ ...u, function: e.target.value }))} />
                  <input className={feld} type="email" placeholder={t('siteDocs.release.email')} value={aktuell.email} onChange={(e) => setAktuell((u) => ({ ...u, email: e.target.value }))} />
                </div>
                {!emailOk(aktuell.email) && <p className="text-sm text-red-700">{t('siteDocs.release.emailInvalid')}</p>}
                <SignatureField
                  key={flaeche}
                  onChange={(bild) => setAktuell((u) => ({ ...u, bild }))}
                  label={t('siteDocs.release.signature')}
                  loeschenLabel={t('siteDocs.release.clear')}
                  zuKurzLabel={t('siteDocs.release.tooShort')}
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {fehlendeRollen.length > 0 ? (
                    <p className="text-sm text-amber-800">{t('siteDocs.release.missingSigners', { roles: fehlendeRollen.map((r) => t(`siteDocs.roles.${r}`)).join(', ') })}</p>
                  ) : (
                    <span />
                  )}
                  <button type="button" disabled={!vollstaendig(aktuell)} onClick={weitere} className="h-9 rounded-md border px-3 text-sm disabled:opacity-50">
                    {t('siteDocs.release.another')}
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
